import React, { useMemo } from 'react';
import {
  LTIconButton,
  LTTag,
  LTText,
  LTBox,
  LTButton
} from '@lambdatestincprivate/lt-components';
import RCAErrorAnalytics from './RCAErrorAnalytics';
import RCAProgressiveLoader from './RCAProgressiveLoader';
import RCASkeletonLoader from './RCASkeletonLoader';
import RCATimeline from './RCATimeline';
import RCAFixSection from './RCAFixSection';
import RCAFeedback from './RCAFeedback';
import RCAErrorHandler from './RCAErrorHandler';
import styles from './AIRca.module.css';
import TestMuAIIcon from '../../assets/TestMuAIIcon';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from '@primer/octicons-react';
import RCAStatusIcon from './RCAStatusIcon';

const RCADrawerContent = ({
  isLoading,
  isGenerating,
  isError,
  errorTitle,
  errorMessage,
  errorType,
  rcaData,
  testName,
  subTitle,
  status,
  testId,
  product,
  errorRepetitionData,
  errorTrendsData,
  onClose,
  onRetry,
  onRefetch,
  onGenerate,
  submitFeedback,
  rcaCategory,
  baseUrl,
  totalTests,
  activeTestIndex,
  onTestNavigate,
}) => {
  const showNavigation = totalTests > 1 && onTestNavigate;
  const prefilledFeedback = useMemo(() => {
    if (!rcaData || !testId) return null;
    return {
      test_id: testId,
      rca_category: rcaCategory,
      summary: rcaData?.failure_summary,
      failure: rcaData?.parent_failure_category,
      rootCause: rcaData?.root_cause_category,
      additional_feedback: rcaData?.additional_feedback
    };
  }, [rcaData, testId, rcaCategory]);

  return (
    <div className={styles.wrapper}>
      {/* Fixed Header */}
      <div className={styles.headerSection}>
        <div className={styles.headerBar}>
          <div className={styles.headerRow}>
            <div className={styles.titleGroup}>
              <div className={styles.titleInner}>
                <TestMuAIIcon variant={TestMuAIIcon.Variant.SPARKLE} />
                <LTText
                  text="RCA"
                  variant={LTText.TextVariant.HEADER_BOLD}
                  color="text.default"
                />
              </div>
              <LTTag
                text="Experimental"
                variant={LTTag.Variant.blueAccent}
                size={LTTag.Size.SMALL}
                hideTooltip
                customStyle={{
                  border: 'none',
                  color: '#0969da',
                  backgroundColor: '#ddf4ff',
                  fontWeight: 500
                }}
              />
            </div>
            {onClose && (
              <LTIconButton
                icon={XIcon}
                onClick={onClose}
                variant={LTIconButton.IconButtonVariants.INVISIBLE}
                aria-label="Close drawer"
              />
            )}
          </div>
        </div>
        {testName && !errorTitle && (
          <div className={styles.testNameBar}>
            <RCAStatusIcon status={status} />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                overflow: 'hidden',
                flex: '1 1 0',
                minWidth: 0,
              }}
            >
              <LTText
                text={testName}
                variant={LTText.TextVariant.SUBHEADER_BOLD}
                color="text.default"
              />
              {subTitle && (
                <LTText
                  text={subTitle}
                  variant={LTText.TextVariant.SUBHEADER_REGULAR}
                  color="text.muted"
                />
              )}
            </div>
            {showNavigation && (
              <LTBox
                styles={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                }}
              >
                <LTIconButton
                  icon={ChevronLeftIcon}
                  onClick={() => onTestNavigate(activeTestIndex - 1)}
                  variant={LTIconButton.IconButtonVariants.INVISIBLE}
                  size={LTIconButton.Size.SMALL}
                  disabled={activeTestIndex <= 0}
                  aria-label="Previous test"
                />
                <LTText
                  variant={LTText.TextVariant.SUBHEADER_REGULAR}
                  color="text.muted"
                  text={`${activeTestIndex + 1} / ${totalTests}`}
                />
                <LTIconButton
                  icon={ChevronRightIcon}
                  onClick={() => onTestNavigate(activeTestIndex + 1)}
                  variant={LTIconButton.IconButtonVariants.INVISIBLE}
                  size={LTIconButton.Size.SMALL}
                  disabled={activeTestIndex >= totalTests - 1}
                  aria-label="Next test"
                />
              </LTBox>
            )}
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className={styles.scrollableContent}>
        {isLoading && (
          <div className={styles.loaderWrap}>
            {/* Progress loader only while actively generating; a plain fetch of
                an already-generated RCA shows the skeleton instead. */}
            {isGenerating ? <RCAProgressiveLoader /> : <RCASkeletonLoader />}
          </div>
        )}

        {!isLoading && isError && (
          <RCAErrorHandler
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            errorType={errorType}
            onRetry={onRetry}
          />
        )}

        {!isLoading && !isError && !rcaData && onGenerate && (
          <LTButton
            leadingIcon={<TestMuAIIcon />}
            variant={LTButton.Variant.AI}
            size={LTButton.Size.MEDIUM}
            onClick={onGenerate}
            className={styles.generateRcaButton}
          >
            Generate RCA
          </LTButton>
        )}

        {rcaData && (
          <div className={styles.contentBody}>
            <RCAErrorAnalytics
              errorRepetitionData={errorRepetitionData}
              errorTrendsData={errorTrendsData}
              rcaData={rcaData}
              baseUrl={baseUrl}
            />
            <RCATimeline timelineData={rcaData?.error_timeline} />
            <RCAFixSection sections={rcaData?.steps_to_fix} />
          </div>
        )}
      </div>

      {/* Feedback Footer */}
      {!isLoading && rcaData && (
        <RCAFeedback
          testId={testId}
          product={product}
          prefilledFeedback={prefilledFeedback}
          feedbackProvided={rcaData?.feedback_provided}
          onSubmitFeedback={submitFeedback}
          onRefetch={onRefetch || onRetry}
          onRefetchRCAData={onRefetch || onRetry}
          onRetry={onRetry}
        />
      )}
    </div>
  );
};

export default RCADrawerContent;
